import { HttpParams } from '@angular/common/http';
import * as R from 'ramda';
import { FormControl, FormGroup, FormArray } from '@angular/forms';
import { DataType } from '../constants/data-type';
import { ControlType } from '../constants/control-type';
import { FieldType } from '../constants/field-type';
import moment from 'moment';
import { inject } from '@angular/core';
import { DateService } from '../services/date.service';
import { LocalStorageConstants } from '../constants/local-storage';

export const isNotEmpty = R.complement(R.isEmpty);
export const isNotNil = R.complement(R.isNil);
export const isNotNilAndNotEmpty = R.both(isNotNil, isNotEmpty);
export const isNilOrEmpty = R.either(R.isNil, R.isEmpty);
export const mapIndex = R.addIndex(R.map as any);
export const returnUndefined = () => undefined;
export const returnValue = (value: any) => () => value;
export const sameValue = (value: any) => value;
export const defaultValueIfNil = R.curry((value: any, current: any): any => {
    return R.ifElse(isNotNil, () => current, () => value)(current);
});
export const applyIfNotNil = (fn) => R.ifElse(isNilOrEmpty, R.empty, fn);
export const isString = (value: any) => { if (typeof value === 'string' || value instanceof String) { return true; } else return false };
export const isBoolean = (value: any) => { return value === false || value === true; };

export const isHidden = R.compose(R.equals(true), R.prop('hidden'));
export const isNotHidden = R.complement(isHidden);
export const isAvailable = R.compose(R.equals(true), R.prop('available'));
export const isNotAvailable = R.complement(isAvailable);
export const isVisible = R.compose(R.equals(true), R.prop('visible'));


export const isStringDataType = R.compose(R.equals(DataType.String), R.prop('type'));
export const isListDataType = R.compose(R.equals(DataType.List), R.prop('type'));
export const isLinkDataType = R.compose(R.equals(DataType.Link), R.prop('type'));
export const isBooleanDataType = R.compose(R.equals(DataType.Boolean), R.prop('type'));
export const isIntegerDataType = R.compose(R.equals(DataType.Integer), R.prop('type'));
export const isBigDecimalDataType = R.compose(R.equals(DataType.BigDecimal), R.prop('type'));
export const isHTMLDataType = R.compose(R.equals(DataType.HTML), R.prop('type'));
export const isDateDataType = R.compose(R.equals(ControlType.Date), R.prop('type'));
export const isDateRangeDataType = R.compose(R.equals(ControlType.DateRange), R.prop('type'));
export const isDateRangeListType = R.compose(R.equals(DataType.dateRangeList), R.prop('type'));

export const isPickListControlType = R.compose(R.equals(ControlType.Picklist), R.prop('controlType'));
export const isMultiSelectControlType = R.compose(R.equals(ControlType.MultiSelect), R.prop('controlType'));

export const isNativeFieldType = R.compose(R.equals(FieldType.NATIVE), R.prop('fieldType'));
export const isCompositeFieldType = R.compose(R.equals(FieldType.COMPOSITE), R.prop('fieldType'));
export const isCustomFieldType = R.compose(R.equals(FieldType.CUSTOM), R.prop('fieldType'));
export const isSeparatorFieldType = R.compose(R.equals(FieldType.SEPARATOR), R.prop('fieldType'));


export const isNotVisible = R.complement(isVisible);

export const isRoleColumn = R.compose(R.equals('role'), R.prop('name'));
export const isStatusColumn = R.compose(R.equals('status'), R.prop('name'));
export const isDisplayNameColumn = R.compose(R.equals('displayName'), R.prop('name'));

export const hasValue = R.compose(R.both(isNotNil, isNotEmpty), R.prop('value'));


export const appendHttpParam = R.curry((httpParams: HttpParams, paramName: string, paramValue: string) => httpParams.append(paramName, paramValue));

export const appendHttpParamIfNotEmpty = R.curry((httpParams: HttpParams, paramName: string, paramValue: string) => {
    return R.ifElse(R.both(isNotNil, isNotEmpty), appendHttpParam(httpParams, paramName), returnValue(httpParams))(paramValue);
});

export const idParamsIsNotNil = (params: { [key: string]: any }) => isNotNil(params['id']);


// Local storage utility functions
export const setItemsIntoLocalStorage = (items: { [key: string]: any }) => {
    R.keys(items).forEach(key => {
        localStorage.setItem(key.toString(), items[key]);
    });
};

export const removeItemsFromLocalStorage = (keys: Array<string>) => {
    R.forEach((key: string) => localStorage.removeItem(key), keys);
};

export const getItemFromLocalStorage = (itemName: string) => {
    return localStorage.getItem(itemName);
};

export const hasItemInLocalStorage = (itemName) => {
    return isNotNil(getItemFromLocalStorage(itemName));
};


const DESTINATION_URL = 'destinationURL';

export const setDestinationURL = (destinationURL: string) => {
    setItemsIntoLocalStorage({ [DESTINATION_URL]: destinationURL });
};

export const getDestinationURL = (): string => {
    return getItemFromLocalStorage(DESTINATION_URL) ?? '';
};

export const removeDestinationURL = () => {
    removeItemsFromLocalStorage([DESTINATION_URL]);
};

/**
 * Minimizes an object with an associated entity
 *
 * @param object
 * @param properties
 */
export const minimize = (object: any, ...properties: any[]) => {
    properties.forEach((property) => {
        if (object[property] && object[property].hasOwnProperty('id')) {
            object[property] = {
                id: object[property]['id']
            };
        }
    });
};

export function detectIE() {
    const ua = window.navigator.userAgent;

    // Test values; Uncomment to check result …

    // IE 10
    // ua = 'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.2; Trident/6.0)';

    // IE 11
    // ua = 'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko';

    // Edge 12 (Spartan)
    // ua = 'Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/39.0.2171.71 Safari/537.36 Edge/12.0';

    // Edge 13
    // ua = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/46.0.2486.0 Safari/537.36 Edge/13.10586';

    const msie = ua.indexOf('MSIE ');
    if (msie > 0) {
        // IE 10 or older => return version number
        return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
    }

    const trident = ua.indexOf('Trident/');
    if (trident > 0) {
        // IE 11 => return version number
        const rv = ua.indexOf('rv:');
        return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
    }

    return false;
}

export const appendPageAndSizeParamsIfNotEmpty = (httpParams: HttpParams, page: number, size: number) => {
    const appendPageAndSizeParams = () => {
        httpParams = appendHttpParam(httpParams, 'page', JSON.stringify({"Size":R.toString(size), "Page" : R.toString(page) }));
        return httpParams;
    };
    return R.ifElse(isNotNil, appendPageAndSizeParams, returnValue(httpParams))(page);
};

export const appendParamsIfNotEmpty = (httpParams: HttpParams, page: number, size: number) => {
    const appendPageAndSizeParams = () => {
        httpParams = appendHttpParam(httpParams, 'page', JSON.stringify({"Size":R.toString(size), "Page" : R.toString(page) }));
        return httpParams;
    };
    return R.ifElse(isNotNil, appendPageAndSizeParams, returnValue(httpParams))(page);
};

export const appendSortParamIfNotEmpty = (httpParams: HttpParams, sort: any[] = [], defaultSort: any) => {
    var sortField : string = "";
    var ascDesc : string  = "";
    if(sort && sort.length && typeof(sort) === 'string'){
        sortField = (<string>sort).split(',')[0].replace("_1", "");
        ascDesc = (<string>sort).split(',')[1];
    }
    const appendSortParam = (sortFieldParam: string) => httpParams.append('sort', `${sortField},${ascDesc}`);
    return R.ifElse(isNotNil, appendSortParam, returnValue(httpParams))(defaultSort);
};

export const appendQueryParamIfNotEmpty = (httpParams: HttpParams, queryParam: string = '') => {
    return R.ifElse(queryParamIsNotEmpty, appendHttpParam(httpParams, 'query'), returnValue(httpParams))(queryParam);
};

export const appendFieldsParamIfNotEmpty = (httpParams: HttpParams, fields: string = '') => {
    return R.ifElse(queryParamIsNotEmpty, appendHttpParam(httpParams, 'fields'), returnValue(httpParams))(fields);
};


export const appendHttpParamsIfNotEmpty = (httpParams: HttpParams, paramName: string, paramValue: string) => {
    return R.ifElse(isNotNilAndNotEmpty, appendHttpParam(httpParams, paramName), returnValue(httpParams))(paramValue);
};

const EMPTY_RSQL_QUERY = /^\(?\s*\)?$/;
const queryParamIsEmpty = R.compose(isNotEmpty, R.ifElse(isNotNil, R.match(EMPTY_RSQL_QUERY), returnValue([])));
const queryParamIsNotEmpty = R.complement(queryParamIsEmpty);

export const appendSortIfNotEmpty = (httpParams: HttpParams, sort: Array<string> = []) => {
    var sortField : string = "";
    var ascDesc : string  = "";
    if(sort && sort.length && typeof(sort) === 'string'){
        sortField = (<string>sort).split(',')[0].replace("_1", "");
        ascDesc = (<string>sort).split(',')[1];
    }
    return (sort && sort.length > 0) ? httpParams.append('sort', `${sortField},${ascDesc}`) : httpParams;
};

// export const appendSortFromModelIfNotEmpty = (httpParams: HttpParams, sort: any = []) => {
//     const tmp = [];
//     if(sort && sort.length > 0){
//         for(var i in sort){
//             const item = sort[i];
//             tmp.push({
//                 "field" : item.colId,
//                 "order" : item.sort,
//             })
//         };
//     }
//     return (sort && sort.length > 0) ? httpParams.append('sort', ` ${JSON.stringify(tmp)} `) : httpParams;
// }; 

export const sortByName = (sort: string) => {
    return R.ifElse(
        notNilAndEqualsToDisplayName,
        () => { },
        sortByDisplayName
    )(sort);
};

export const notNil = R.compose(R.not, R.isNil);
export const notNilAndEqualsToDisplayName = R.both(notNil, R.equals('displayName'));
export const notNilAndEqualsToOrganizationalUnitId = R.both(notNil, R.equals('organizationalUnitId'));
export const sortByDisplayName = R.ifElse(notNilAndEqualsToOrganizationalUnitId, () => 'name', returnUndefined);


export const getErrorMessage = (fieldErrors: { path: string, error: string }[]) => {
    return R.map((fieldError) => fieldError.error + '\n', fieldErrors);
};


export const hasChildren = R.compose(R.equals(true), R.prop('hasChildren'));
export const hasNotChildren = R.complement(hasChildren);
export const isExpanded = R.compose(R.equals(true), R.prop('expanded'));
export const isNotExpanded = R.complement(isExpanded);
export const hasChildrenAndIsNotExpanded = R.both(hasChildren, isNotExpanded);
export const findIndexById = (id) => R.findIndex(R.propEq('id', id));
export const filterByFieldValue = (field: string, value: string) => R.compose(R.equals(field), R.prop(value));

export const textToLines = (inputString : string) => {
    const vals = inputString?.length ? inputString.trim().split('\n') : []
    return vals.filter(value=> !!value.length).map(value=> value.trim()).filter(value=> !!value.length);
}

export const noWhitespaceValidator = (control: FormControl) => {
	const isWhitespace = (control.value || '').trim().length === 0;
	const isValid = !isWhitespace;
	return isValid ? null : { 'whitespace': true };
}

// Password validation utility for reactive forms
export const passwordValidator = (control: any): any => {
    const value = control.value;
    if (!value) {
        return null;
    }

    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumeric = /[0-9]/.test(value);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);

    const passwordValid = hasUpperCase && hasLowerCase && hasNumeric && hasSpecialChar;

    if (!passwordValid) {
        return {
            passwordStrength: {
                hasUpperCase,
                hasLowerCase,
                hasNumeric,
                hasSpecialChar
            }
        };
    }

    return null;
};

// Email validation utility for reactive forms
export const emailValidator = (control: any): any => {
    const value = control.value;
    if (!value) {
        return null;
    }

    // RFC 5322 compliant email regex pattern
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    if (!emailRegex.test(value)) {
        return {
            invalidEmail: {
                message: 'Please enter a valid email address'
            }
        };
    }

    return null;
};

// Email validation check
export const isValidEmail = (email: string): boolean => {
    if (!email) return false;
    
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email);
};

export const capitalizeFirstChar = (val: unknown): string => {
  if (val === null || val === undefined) return "";
  const str = String(val);
  if (!str) return "";
  return str?.charAt(0)?.toUpperCase() + str?.slice(1);
};

export const getCurrentRole = (): string => {
    const currentUser =  localStorage.getItem(LocalStorageConstants.currentUser);
    if (currentUser) {
        const user = JSON.parse(currentUser) ?? '';
        if (!user || !user.role || user.role.length === 0) return "";
        return user.role;
    }
    return "";
};


export const isResident = (): boolean => {
    const currentUser =  localStorage.getItem(LocalStorageConstants.currentUser);
    if (currentUser) {
        const user = JSON.parse(currentUser) ?? '';
        if (!user || !user.role || user.role.length === 0) return false;
        return user.role === 'Resident';
    }
    return false;
}

export const isSecurityGuard = (): boolean => {
    const currentUser =  localStorage.getItem(LocalStorageConstants.currentUser);
    if (currentUser) {
        const user = JSON.parse(currentUser) ?? '';
        if (!user || !user.role || user.role.length === 0) return false;
        return user.role === 'Security Guard';
    }
    return false;
}

export const isAssetManager = (): boolean => {
    const currentUser =  localStorage.getItem(LocalStorageConstants.currentUser);
    if (currentUser) {
        const user = JSON.parse(currentUser) ?? '';
        if (!user || !user.role || user.role.length === 0) return false;
        return user.role === 'Asset Manager';
    }
    return false;
}

export const isSuperAdmin = (): boolean => {
    const currentUser =  localStorage.getItem(LocalStorageConstants.currentUser);
    if (currentUser) {
        const user = JSON.parse(currentUser) ?? '';
        if (!user || !user.role || user.role.length === 0) return false;
        return user.role === 'Super Admin';
    }
    return false;
}

/**
 * Trims all string values in a FormGroup
 * @param formGroup - The FormGroup to process
 * @param fieldsToTrim - Optional array of specific field names to trim. If not provided, all string fields will be trimmed
 * @returns Object with trimmed values
 */
export const trimFormValues = (formGroup: FormGroup, fieldsToTrim?: string[]): any => {
    const formValues = formGroup.getRawValue();
    const trimmedValues = { ...formValues };
    
    const fields = fieldsToTrim || Object.keys(formValues);
    
    fields.forEach(field => {
        const value = trimmedValues[field];
        if (value && typeof value === 'string') {
            trimmedValues[field] = value.trim();
        }
    });
    
    return trimmedValues;
}

/**
 * Trims string values in a FormGroup and updates the form controls
 * @param formGroup - The FormGroup to process
 * @param fieldsToTrim - Optional array of specific field names to trim. If not provided, all string fields will be trimmed
 */
export const trimFormFields = (formGroup: FormGroup, fieldsToTrim?: string[]): void => {
    const fields = fieldsToTrim || Object.keys(formGroup.controls);
    
    fields.forEach(field => {
        const control = formGroup.get(field);
        if (control && control.value && typeof control.value === 'string') {
            const trimmedValue = control.value.trim();
            if (trimmedValue !== control.value) {
                control.setValue(trimmedValue);
            }
        }
    });
}

/**
 * Automatically finds and trims all string fields in a FormGroup, including nested FormGroups and FormArrays
 * @param formGroup - The FormGroup or FormArray to process
 * @param excludeFields - Optional array of field names to exclude from trimming
 * @param visited - Set of already visited objects to prevent circular references (internal use)
 * @param depth - Current recursion depth to prevent stack overflow (internal use)
 */
export const autoTrimFormFields = (
    formGroup: FormGroup | FormArray, 
    excludeFields: string[] = [],
    visited: WeakSet<any> = new WeakSet(),
    depth: number = 0
): void => {
    // Prevent stack overflow from deep recursion
    const MAX_DEPTH = 20;
    if (depth > MAX_DEPTH) {
        // console.warn('autoTrimFormFields: Maximum recursion depth reached');
        return;
    }
    
    // Prevent circular reference infinite loops
    if (visited.has(formGroup)) {
        return;
    }
    visited.add(formGroup);
    
    try {
        if (formGroup instanceof FormArray) {
            // Handle FormArray
            formGroup.controls.forEach((control, index) => {
                if (!control) return;
                
                if (control instanceof FormGroup) {
                    // Recursively handle FormGroups inside FormArray
                    autoTrimFormFields(control, excludeFields, visited, depth + 1);
                } else if (control instanceof FormArray) {
                    // Recursively handle nested FormArrays
                    autoTrimFormFields(control, excludeFields, visited, depth + 1);
                } else if (control.value !== null && control.value !== undefined) {
                    // Trim string values in FormArray
                    if (typeof control.value === 'string') {
                        try {
                            const trimmedValue = control.value.trim();
                            if (trimmedValue !== control.value) {
                                control.setValue(trimmedValue);
                            }
                        } catch (e) {
                            // console.error(`Error trimming value at index ${index}:`, e);
                        }
                    }
                }
            });
        } else if (formGroup instanceof FormGroup) {
            // Handle FormGroup
            Object.keys(formGroup.controls).forEach(key => {
                // Skip if field is in exclude list
                if (excludeFields.includes(key)) {
                    return;
                }
                
                const control = formGroup.get(key);
                if (!control) return;
                
                if (control instanceof FormGroup) {
                    // Recursively handle nested FormGroups
                    autoTrimFormFields(control, excludeFields, visited, depth + 1);
                } else if (control instanceof FormArray) {
                    // Recursively handle FormArrays
                    autoTrimFormFields(control, excludeFields, visited, depth + 1);
                } else if (control.value !== null && control.value !== undefined) {
                    // Only trim if it's a string value
                    if (typeof control.value === 'string') {
                        try {
                            const trimmedValue = control.value.trim();
                            if (trimmedValue !== control.value) {
                                control.setValue(trimmedValue);
                            }
                        } catch (e) {
                            // console.error(`Error trimming field ${key}:`, e);
                        }
                    }
                }
            });
        }
    } catch (error) {
        // console.error('Error in autoTrimFormFields:', error);
    }
}

/**
 * Automatically finds all string values in a FormGroup and returns trimmed values, including nested FormGroups
 * @param formGroup - The FormGroup to process
 * @param excludeFields - Optional array of field names to exclude from trimming
 * @returns Object with all values, with string values trimmed
 */
export const autoTrimFormValues = (formGroup: FormGroup, excludeFields: string[] = []): any => {
    try {
        const formValues = formGroup.getRawValue();
        const visited = new WeakSet();
        
        const trimValues = (obj: any, path: string = '', depth: number = 0): any => {
            // Handle for infine loop
            const MAX_DEPTH = 20;
            if (depth > MAX_DEPTH) {
                // console.warn('autoTrimFormValues: Maximum recursion depth reached');
                return obj;
            }
            
            if (obj === null || obj === undefined) {
                return obj;
            }
            
            // Handle primitives
            if (typeof obj === 'string') {
                try {
                    // Check if current path should be excluded
                    const currentField = path.split('.').pop()?.replace(/\[\d+\]/, '') || '';
                    return excludeFields.includes(currentField) ? obj : obj.trim();
                } catch (e) {
                    // console.error(`Error trimming string at path ${path}:`, e);
                    return obj;
                }
            }
            
            if (typeof obj === 'number' || typeof obj === 'boolean') {
                return obj;
            }
            
            // Handle Date objects
            if (obj instanceof Date) {
                return obj;
            }
            
            // Prevent circular references for objects
            if (typeof obj === 'object' && obj !== null) {
                // Check for circular reference
                if (visited.has(obj)) {
                    // console.warn(`Circular reference detected at path: ${path}`);
                    return obj;
                }
                visited.add(obj);
                
                // Handle arrays
                if (Array.isArray(obj)) {
                    try {
                        return obj.map((item, index) => trimValues(item, `${path}[${index}]`, depth + 1));
                    } catch (e) {
                        // console.error(`Error processing array at path ${path}:`, e);
                        return obj;
                    }
                }
                
                // Handle objects
                try {
                    const trimmedObj: any = {};
                    Object.keys(obj).forEach(key => {
                        const newPath = path ? `${path}.${key}` : key;
                        if (!excludeFields.includes(key)) {
                            trimmedObj[key] = trimValues(obj[key], newPath, depth + 1);
                        } else {
                            trimmedObj[key] = obj[key];
                        }
                    });
                    return trimmedObj;
                } catch (e) {
                    // console.error(`Error processing object at path ${path}:`, e);
                    return obj;
                }
            }
            
            // Fallback for any other type
            return obj;
        };
        
        return trimValues(formValues);
    } catch (error) {
        // console.error('Error in autoTrimFormValues:', error);
        // Return original values if error occurs
        return formGroup.getRawValue();
    }
}

/**
 * Converts a base64 string to a File object
 * @param base64String - The base64 string (with or without data URL prefix)
 * @param fileName - The desired filename for the file
 * @param mimeType - The MIME type of the file (optional, will be inferred from base64 if not provided)
 * @returns File object
 */
export const base64ToFile = (base64String: string, fileName: string, mimeType?: string): File => {
    // Remove data URL prefix if present (e.g., "data:image/jpeg;base64,")
    const base64Data = base64String.includes(',') ? base64String.split(',')[1] : base64String;
    
    // Infer MIME type from data URL if not provided
    let inferredMimeType = mimeType;
    if (!inferredMimeType && base64String.includes('data:')) {
        const dataUrlMatch = base64String.match(/data:([^;]+);/);
        if (dataUrlMatch) {
            inferredMimeType = dataUrlMatch[1];
        }
    }
    
    // Default to image/jpeg if no MIME type can be determined
    const finalMimeType = inferredMimeType || 'image/jpeg';
    
    // Convert base64 to binary
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    
    // Create and return File object
    return new File([byteArray], fileName, { type: finalMimeType });
};